import { Navigate, Route, useLocation, useParams } from "react-router-dom";
import { appRoutes } from "./appRoutes";
import { referenceAdminRoutePaths } from "./referenceAdminRoutes";

function RedirectTo({ to }: { to: string }) {
    const location = useLocation();
    return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

function RedirectWithRouteParam({ toTemplate }: { toTemplate: string }) {
    const { id } = useParams();
    const location = useLocation();
    const to = toTemplate.replace(":id", encodeURIComponent(id ?? ""));
    return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

/**
 * Legacy URL redirects for bookmarks and external links.
 * Register after canonical routes inside `<Routes>`.
 */
export function legacyRouteElements() {
    const { ewcCodes, geography, municipalDegradables } = referenceAdminRoutePaths;

    return (
        <>
            <Route path="/movement-parties" element={<RedirectTo to={appRoutes.transportParties} />} />
            <Route path="/reference-data" element={<RedirectTo to={appRoutes.codes} />} />
            <Route path="/admin/foundation" element={<RedirectTo to={appRoutes.systemSetup} />} />

            <Route path="/ewc-codes" element={<RedirectTo to={ewcCodes.list} />} />
            <Route path="/ewc-codes/new" element={<RedirectTo to={ewcCodes.new!} />} />
            <Route path="/ewc-codes/:id" element={<RedirectWithRouteParam toTemplate={ewcCodes.edit} />} />

            <Route path="/geography" element={<RedirectTo to={geography.list} />} />
            <Route path="/geography/new" element={<RedirectTo to={geography.new!} />} />
            <Route path="/geography/:id" element={<RedirectWithRouteParam toTemplate={geography.edit} />} />

            <Route
                path="/municipal-degradables"
                element={<RedirectTo to={municipalDegradables.list} />}
            />
            <Route
                path="/municipal-degradables/:id"
                element={<RedirectWithRouteParam toTemplate={municipalDegradables.edit} />}
            />

            <Route path="/websitesettings" element={<RedirectTo to={appRoutes.websiteSettings} />} />
            <Route path="/EmailTemplates" element={<RedirectTo to={appRoutes.automaticEmails} />} />
            <Route path="/EmailTemplates/edit" element={<RedirectTo to={appRoutes.automaticEmailsEdit} />} />
            <Route path="/administration" element={<RedirectTo to={appRoutes.admin} />} />
            <Route path="/diary" element={<RedirectTo to={appRoutes.companyDiary} />} />
            <Route path="/mytasks" element={<RedirectTo to={appRoutes.myTasks} />} />
            <Route path="/mycalendar" element={<RedirectTo to={appRoutes.myCalendar} />} />
            <Route path="/task/:id" element={<RedirectWithRouteParam toTemplate="/tasks/:id" />} />
            <Route path="/sessions" element={<RedirectTo to={appRoutes.userSessions} />} />
        </>
    );
}

/** Documented old → new path map (static paths only; param routes listed separately). */
export const legacyRouteRedirectMap: Readonly<Record<string, string>> = {
    "/movement-parties": appRoutes.transportParties,
    "/reference-data": appRoutes.codes,
    "/admin/foundation": appRoutes.systemSetup,
    "/ewc-codes": referenceAdminRoutePaths.ewcCodes.list,
    "/ewc-codes/new": referenceAdminRoutePaths.ewcCodes.new!,
    "/geography": referenceAdminRoutePaths.geography.list,
    "/geography/new": referenceAdminRoutePaths.geography.new!,
    "/municipal-degradables": referenceAdminRoutePaths.municipalDegradables.list,
    "/websitesettings": appRoutes.websiteSettings,
    "/EmailTemplates": appRoutes.automaticEmails,
    "/EmailTemplates/edit": appRoutes.automaticEmailsEdit,
    "/administration": appRoutes.admin,
    "/diary": appRoutes.companyDiary,
    "/mytasks": appRoutes.myTasks,
    "/mycalendar": appRoutes.myCalendar,
    "/sessions": appRoutes.userSessions,
};

/** Parametric legacy redirects: pattern → template. */
export const legacyParamRouteRedirectMap: Readonly<Record<string, string>> = {
    "/ewc-codes/:id": referenceAdminRoutePaths.ewcCodes.edit,
    "/geography/:id": referenceAdminRoutePaths.geography.edit,
    "/municipal-degradables/:id": referenceAdminRoutePaths.municipalDegradables.edit,
    "/task/:id": "/tasks/:id",
};
